import './index.less';
import * as React from 'react';
import { useState, useEffect, useRef, useMemo } from 'react';
// @ts-ignore
import PropTypes from 'prop-types';
import { Modal, Button, Space, Message, Alert } from '@bedrock/components'
import { TabContent } from '../components';
import { showAlert } from 'utils';
import _ from 'lodash';
import messages from 'messages/Workbench/ProjectDetail';
import NewCooperate from '@mpc/common/src/features/NewCooperate';
import StatementAttachmentItem from '@mpc/common/src/features/StatementAttachmentItem';
import UploaderMarketingWorks from 'containers/OutWorkbench/components/UploaderMarketingWorks';
import UploadHeatingBoostWorks from 'containers/OutWorkbench/components/UploadHeatingBoostWorks';
import QuotationSummary, { SubmitCaseReportPopover } from '@mpc/common/src/features/QuotationSummary';
import { localPx2rem } from '@mpc/common/src/utils';
import { useDrawerState } from 'model/drawerState';
import { outsourceDemandMoveMove, outsourceDemandHideInvoice } from '@/services/api';
import { Check, RollTopLeft } from '@bedrock/icons-react';
import { useMemoizedFn, useRequest } from 'ahooks';
import { SubmitModal } from '@mpc/common/src/components';
import { isConditionalPass, isReject } from '@mpc/common/src/components/AIAcceptance/constants';

export interface MakingProps {
  topSlot?: React.ReactNode;
  tabKey: string;
  templateDownloadUrl?: string;
  handleSubmitBody?: (submitBody: any) => any;
  attachementUploadUrl?: string;
}

interface MakingContextProps {
  intl: any;
  prefixCls: string;
  menuKey: string;
  detail: any;
  loadDetail: Function;
  projectMove: Function;
  onResize: Function;
}

const Making: React.FC<MakingProps & MakingContextProps> = (props) => {
  const {
    topSlot = undefined,
    tabKey,
    templateDownloadUrl,
    handleSubmitBody,
    attachementUploadUrl,
    intl,
    prefixCls,
    menuKey,
    detail,
    loadDetail,
    projectMove,
    onResize,
  } = props;

  const formRef = useRef<any>(null);
  const cooperateRef = useRef<any>(null);
  const quotationSummaryRef = useRef<any>(null);
  const statementAttachmentRef = useRef<any>(null);
  const attachmentRef = useRef<any>(null);

  const [submitting, setSubmitting] = useState(false);

  const [cooperateData, setCooperateData] = useState<any>(null);
  const [statementAttachment, setStatementAttachment] = useState<any>({
    invoice_belong_area: undefined,
    is_oversea_invoice: undefined,
    invoice_list: [],
    signed_statement_attachs: [],
  });
  const [quoteSettleReports, setQuoteSettleReports] = useState<any[] | undefined>(undefined);

  // onResize effect
  useEffect(() => {
    if (onResize) {
      onResize();
    }
  }, [onResize]);

  // componentDidMount logic
  useEffect(() => {
    const { signed_statement_attachs, sub_state } = detail;

    const { setIsDrawerEditing } = useDrawerState.getState();
    if ([undefined, 'NONE'].includes(sub_state?.toUpperCase())) {
      setIsDrawerEditing(true);
    }

    setStatementAttachment((prev: any) => ({
      ...prev,
      signed_statement_attachs: signed_statement_attachs,
    }));

    return () => {
      const { setIsDrawerEditing } = useDrawerState.getState();
      setIsDrawerEditing(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Equivalent to componentDidMount, run once

  // Initialize cooperateData logic from render
  const defaultPlanInfo = useMemo(() => {
    if (!detail?.project_plan_info) return null;
    return {
      ...detail.project_plan_info,
      cooperate_accounts: detail.project_plan_info?.cooperate_accounts?.map((v: any) => {
        const item = detail?.demand_suppliers?.[0]?.demand_supplier_quotes?.find?.((it: any) => {
          return it.cooperate_account_id === v.id;
        });
        let $supplier_settle_price;
        if (detail?.need_cooperate_account_supplier_settle_price) {
          $supplier_settle_price = {
            value: item?.need_actual_execution ? item?.discount_quote_price_with_tax : 0,
            currency: item?.currency,
          };
        }
        // 如果是机审状态下，则需要显示真实填写的数据
        if (![undefined, 'NONE'].includes(detail?.sub_state?.toUpperCase())) {
          $supplier_settle_price = {
            value: item?.supplier_settle_price,
            currency: item?.currency,
          };
        }
        return {
          ...v,
          $supplier_settle_price,
        };
      }),
    };
  }, [detail]);

  useEffect(() => {
    if (!cooperateData && defaultPlanInfo) {
      setCooperateData(defaultPlanInfo);
    }
  }, [cooperateData, defaultPlanInfo]);

  const { data: isHideInvoice } = useRequest(async () => {
    const res = await outsourceDemandHideInvoice({ demand_id: detail.id, settle_price: cooperateData?.cooperate_accounts?.[0]?.$supplier_settle_price?.value })
    return res?.data
  }, {
    ready: !!cooperateData?.cooperate_accounts?.[0]?.$supplier_settle_price?.value,
    refreshDeps: [cooperateData?.cooperate_accounts?.[0]?.$supplier_settle_price?.value],
    debounceWait: 300,
  })

  const handleSubmit = useMemoizedFn(async () => {
    const signed_statement_attachs_blob_keys = statementAttachment?.signed_statement_attachs?.map(
      (item: any) => item?.blob?.key,
    );

    if (cooperateRef.current?.valid() === false) {
      return;
    }

    let summaryValues: any = {};
    if (quotationSummaryRef.current) {
      summaryValues = await quotationSummaryRef.current.validateFields();
    }
    await statementAttachmentRef.current?.validateFields();

    let hasScreenShotError = false;
    let body = {
      id: detail.id,
      making: {
        is_pass: true,
        ...summaryValues,
        invoice_upload_dto: {
          ...statementAttachment,
        },
        signed_statement_attachs_blob_keys,
        ...(cooperateData || {}),
        cooperate_accounts: cooperateData?.cooperate_accounts?.map((v: any) => {
          if (!hasScreenShotError) hasScreenShotError = !!v?.screen_shot_info_error_tags?.length;
          return {
            ...v,
            supplier_settle_price: v?.$supplier_settle_price?.value,
            case_close_report_blob_ids: v?.case_close_report_cooperate_account_attachments?.map(
              (n: any) => n?.blob?.id || n?.id,
            ),
          };
        }),
        // 结案明细
        quote_settle_reports: quoteSettleReports?.map((v: any) => {
          return {
            content: v?.content,
            quote_key: v?.quote_key,
            attach_blob_ids: v?.attachments?.map((n: any) => n?.blob?.id || n?.id).filter(Boolean),
          };
        }),
      },
    };

    const doSubmit = async () => {
      if (handleSubmitBody) {
        body = handleSubmitBody(body);
      }
      setSubmitting(true);
      return projectMove(body, { menuKey })
        .then((res: any) => {
          showAlert({ type: 'success', message: res.res_msg });
          setSubmitting(false);
          loadDetail();
        })
        .catch((error: any) => {
          showAlert({
            type: 'error',
            message: typeof error === 'string' ? error : error.message,
          });
          setSubmitting(false);
        });
    };

    if (hasScreenShotError) {
      Modal.confirm({
        status: 'warning',
        title: '当前识别到截图错误，确定继续提交吗？',
        content: (
          <span>
            识别到截图错误，如错误属实，验收节点将被打回，可能会{' '}
            <span className="making-screen-shot-warning">影响结算时效</span> ，请仔细检查！
          </span>
        ),
        cancelText: '返回检查',
        okText: '确定提交',
        onOk: () => {
          return doSubmit();
        },
        onCancel: () => {
          return;
        },
      });
      return;
    }

    return doSubmit();
  });

  const handleBack = useMemoizedFn(async () => {
    setSubmitting(true);
    try {
      await outsourceDemandMoveMove({
        id: detail.id,
        state: detail.state,
        pass: false,
        note: '',
        form: {
          id: detail?.demand_plan_info?.id,
        },
      });
      Message.success('操作成功');
      loadDetail(detail.id);
      setSubmitting(false);
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  });

  const handlePass = useMemoizedFn(async () => {
    SubmitModal.show({
      title: '确认无问题，不修改',
      maxLength: 500,
      label: '备注说明',
      onCommit: async ({ reason }: { reason: string }) => {
        await outsourceDemandMoveMove({
          id: detail.id,
          state: detail.state,
          pass: true,
          note: reason,
          form: {},
        });
        Message.success('操作成功');
        loadDetail(detail.id);
      },
    });
  });

  const renderBottom = () => {
    const aiConclusion = detail?.demand_aireview_result?.ai_accept_info?.acceptance_conclusion;
    const aiRejectCount = detail?.demand_aireview_result?.ai_accept_info?.ai_reject_count ?? 0;

    // 无 AI 验收结论时，保持原有按钮逻辑
    return (
      <>
        {[undefined, 'NONE'].includes(detail?.sub_state?.toUpperCase()) && (
          <SubmitCaseReportPopover showPopover={detail?.product_area !== 'OVERSEAS'}>
            <Button
              className="pull-right m-t-8"
              type="primary"
              loading={submitting}
              onClick={handleSubmit}
              icon={<Check />}>
              提交结案
            </Button>
          </SubmitCaseReportPopover>
        )}

        {['REVIEW_PASSED', 'REVIEW_ABNORMAL'].includes(detail?.sub_state?.toUpperCase()) && (
          <Space size={12} className="pull-right m-t-8">
            <Button icon={<RollTopLeft />} loading={submitting} type="primary" danger onClick={handleBack}>
              退回修改
            </Button>
            {!(isReject(aiConclusion) && aiRejectCount < 3) && (
              <Button icon={<Check />} loading={submitting} type="primary" onClick={handlePass}>
                确认无问题，不修改
              </Button>
            )}
          </Space>
        )}
      </>
    );
  };

  const bottom = renderBottom();

  return (
    // @ts-ignore
    <TabContent tabKey={tabKey} bottom={bottom} canScroll={true}>
      {topSlot}
      <QuotationSummary
        ref={quotationSummaryRef}
        detail={detail}
        readonly={![undefined, 'NONE'].includes(detail?.sub_state?.toUpperCase())}
        planInfo={cooperateData || defaultPlanInfo || detail?.project_plan_info}
        onChange={(formData: any) => {
          setQuoteSettleReports(formData.quote_settle_reports);
        }}
        editType={'making'}
        components={{
          UploaderMarketingWorks,
          UploadHeatingBoostWorks,
        }}
      />
      {!!detail?.structured && <NewCooperate
        ref={cooperateRef}
        hasTitle={false}
        showBindDataKpi={false}
        readonly={![undefined, 'NONE'].includes(detail?.sub_state?.toUpperCase())}
        detail={detail}
        planInfo={cooperateData || defaultPlanInfo || detail?.project_plan_info}
        category_id={detail.l3_category_id}
        onChange={(planInfo: any) => {
          setCooperateData(planInfo);
        }}
        style={{ marginBottom: localPx2rem(12) }}
      />}
      { detail?.product_area === 'OVERSEAS' ? (
        <div className='making-statement-attachment'>
          {(detail?.non_need_fee_control_verify || isHideInvoice) ? null :
            <Alert
              message='请确保提供与支付款项等额发票，超额开具部分，网易将不予受理退回款申请，亦不可用于抵扣合作尾款或其他合作款项。'
              type='warning'
              showIcon
              style={{ margin: `0 0 20px` }}
            />
          }
          {isHideInvoice && (
            <Alert
              message='预付金额≥实际结算金额，无需上传发票'
              type='info'
              showIcon
              style={{ margin: `0 0 20px` }}
            />
          )}
          <StatementAttachmentItem
            ref={statementAttachmentRef}
            value={statementAttachment}
            view={![undefined, 'NONE'].includes(detail?.sub_state?.toUpperCase())}
            onChange={(e: any) => {
              setStatementAttachment(e);
            }}
            showInvoiceSource={true}
            detail={detail}
            isHideInvoice={isHideInvoice}
          />
        </div>
      ) : null }
    </TabContent>
  );
};

class MakingWrapper extends React.Component<MakingProps, any> {
  public static contextTypes = {
    intl: PropTypes.object,
    prefixCls: PropTypes.string,
    menuKey: PropTypes.string,
    detail: PropTypes.any,
    loadDetail: PropTypes.func,
    projectMove: PropTypes.func,
    onResize: PropTypes.func,
  };

  render() {
    return <Making {...this.props} {...(this.context as any)} />;
  }
}

export default MakingWrapper;
